import type { Connection, PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import { MORFI_OPINION_PROGRAM_ID, SYSTEM_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { deriveMarketPda } from '../pda/derivations.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import { validateOutcomeMetadataText } from '../utils/human-text.js';
import { createBuildResult, invokeMethod, uniqueSignerKeys } from './common.js';
import { deriveCreationPda } from './market-creation-addresses.js';
import type { OutcomePreparation } from './market-preparation.js';

export interface StageOutcomeMetadataParams extends BuildParams {
  creator: PublicKey;
  payer?: PublicKey;
  marketNonce: BN;
  initialOutcomes: number;
  outcomes: readonly OutcomePreparation[];
}

/** Stage initial outcome names before the market account is created. */
export async function buildStageOutcomeMetadata(
  connection: Connection,
  params: StageOutcomeMetadataParams,
): Promise<BuildResult> {
  if (
    !Number.isInteger(params.initialOutcomes) ||
    params.initialOutcomes < 2 ||
    params.initialOutcomes > 10
  ) {
    throw new Error('Metadata staging requires 2..10 initial outcomes');
  }
  if (params.outcomes.length === 0 || params.outcomes.length > params.initialOutcomes) {
    throw new Error('Metadata staging requires 1..initialOutcomes entries');
  }
  if (params.marketNonce.isNeg() || params.marketNonce.bitLength() > 64) {
    throw new Error('marketNonce must fit u64');
  }
  for (const outcome of params.outcomes) validateOutcomeMetadataText(outcome);
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const payer = params.payer ?? params.creator;
  const market = deriveMarketPda(params.creator, params.marketNonce, programId)[0];
  const outcomeMetadata = deriveCreationPda('outcome_metadata', market, programId)[0];
  const program = await getProgram(connection, programId);
  const instruction = await invokeMethod(
    program,
    'stageOutcomeMetadata',
    params.marketNonce,
    params.initialOutcomes,
    params.outcomes.map((outcome) => ({ ...outcome })),
  )
    .accounts({
      creator: params.creator,
      payer,
      market,
      outcomeMetadata,
      systemProgram: SYSTEM_PROGRAM_ID,
    })
    .instruction();
  return createBuildResult(
    [instruction],
    uniqueSignerKeys(params.creator, payer),
    params.computeUnits ?? estimateComputeUnits('stage_outcome_metadata'),
  );
}
